import { apiClient } from './ApiClient'
import { Ticket, PaginatedResponse, TicketStatus, TicketPriority } from '../types/entities'

export interface TicketFilters {
  status?: TicketStatus
  priority?: TicketPriority
  categoryId?: string
  assignedAgentId?: string
  search?: string
  myTickets?: boolean
  organizationId?: string
  childSiteId?: string
}

export interface CreateTicketRequest {
  subject: string
  description: string
  priority: TicketPriority
  categoryId?: string
  tags?: string
  source?: string
  dueDate?: string
  isPrivate?: boolean
  organisationId?: string // Organisation assignment (null = tenant-level)
  childSiteId?: string // Child site assignment (null = tenant or org-level)
  requesterId?: string // For agents creating tickets on behalf of customers
  isGroupTicket?: boolean // Group ticket - visible to all users in same organisation/child site
}

export class TicketService {
  private basePath = '/tickets'

  async getTickets(
    page = 1,
    pageSize = 20,
    filters?: TicketFilters
  ): Promise<PaginatedResponse<Ticket>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString()
    })

    if (filters) {
      if (filters.status) params.append('status', filters.status)
      if (filters.priority) params.append('priority', filters.priority)
      if (filters.categoryId) params.append('categoryId', filters.categoryId)
      if (filters.assignedAgentId) params.append('assignedAgentId', filters.assignedAgentId)
      if (filters.search) params.append('search', filters.search)
      if (filters.myTickets) params.append('myTickets', 'true')
      if (filters.organizationId && filters.organizationId !== 'undefined') params.append('organizationId', filters.organizationId)
      if (filters.childSiteId && filters.childSiteId !== 'undefined') params.append('childSiteId', filters.childSiteId)
    }

    return apiClient.get<PaginatedResponse<Ticket>>(`${this.basePath}?${params}`)
  }

  async getTicket(ticketId: string): Promise<Ticket> {
    return apiClient.get<Ticket>(`${this.basePath}/${ticketId}`)
  }

  async createTicket(request: CreateTicketRequest): Promise<Ticket> {
    return apiClient.post<Ticket>(this.basePath, request)
  }

  async updateTicket(ticketId: string, ticket: Partial<Ticket>): Promise<void> {
    return apiClient.put<void>(`${this.basePath}/${ticketId}`, ticket)
  }

  async deleteTicket(ticketId: string): Promise<void> {
    return apiClient.delete<void>(`${this.basePath}/${ticketId}`)
  }

  // Customer-specific methods
  async getMyTickets(page = 1, pageSize = 20, filters?: Omit<TicketFilters, 'myTickets'>): Promise<PaginatedResponse<Ticket>> {
    return this.getTickets(page, pageSize, { ...filters, myTickets: true })
  }

  // Organization-specific methods for shared visibility
  async getOrganizationTickets(
    organizationId: string,
    page = 1,
    pageSize = 20,
    filters?: Omit<TicketFilters, 'organizationId'>
  ): Promise<PaginatedResponse<Ticket>> {
    return this.getTickets(page, pageSize, { ...filters, organizationId })
  }

  // Oversight user methods for multiple organizations
  async getOversightTickets(
    organizationIds: string[],
    page = 1,
    pageSize = 20,
    filters?: Omit<TicketFilters, 'organizationId'>
  ): Promise<PaginatedResponse<Ticket>> {
    // If multiple organization IDs, make separate calls and combine results
    // Or fetch all oversight tickets without organizationId filter (backend handles this)
    if (organizationIds.length === 1) {
      return this.getOrganizationTickets(organizationIds[0], page, pageSize, filters)
    }
    // For multiple orgs, don't specify organizationId - backend will return all oversight tickets
    return this.getTickets(page, pageSize, filters)
  }

  // Agent-specific methods
  async getAssignedTickets(page = 1, pageSize = 20, filters?: TicketFilters): Promise<PaginatedResponse<Ticket>> {
    // For agents, get tickets assigned to them
    return this.getTickets(page, pageSize, { ...filters, myTickets: true })
  }

  async getAllTickets(page = 1, pageSize = 20, filters?: TicketFilters): Promise<PaginatedResponse<Ticket>> {
    // Get all tickets (for agents/admins to see full queue)
    return this.getTickets(page, pageSize, filters)
  }

  async assignTicket(ticketId: string, agentId?: string): Promise<void> {
    // If agentId not provided, assign to current user (self-assign)
    return apiClient.post<void>(`${this.basePath}/${ticketId}/assign`, agentId ? { agentId } : {})
  }

  async updateTicketStatus(ticketId: string, status: TicketStatus): Promise<void> {
    return apiClient.put<void>(`${this.basePath}/${ticketId}/status`, { status })
  }

  async updateTicketPriority(ticketId: string, priority: TicketPriority): Promise<void> {
    return this.updateTicket(ticketId, { priority })
  }

  // Group ticket watcher management
  async watchTicket(ticketId: string): Promise<void> {
    return apiClient.post<void>(`/ticketwatchers/watch/${ticketId}`, {})
  }

  async unwatchTicket(ticketId: string): Promise<void> {
    return apiClient.delete<void>(`/ticketwatchers/unwatch/${ticketId}`)
  }

  async isWatchingTicket(ticketId: string): Promise<boolean> {
    const response = await apiClient.get<{ data: boolean }>(`/ticketwatchers/is-watching/${ticketId}`)
    return response.data
  }

  async getWatchers(ticketId: string): Promise<WatcherInfo[]> {
    const response = await apiClient.get<{ data: WatcherInfo[] }>(`/ticketwatchers/ticket/${ticketId}`)
    return response.data
  }
}

export interface WatcherInfo {
  watcherId: string
  userId: string
  userName: string
  userEmail: string
  createdAt: string
}

export const ticketService = new TicketService()